import React, {
    useState, useEffect, useCallback, useContext
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { ViewModelServiceContext, ViewModelForm } from 'gw-portals-viewmodel-react';
import { useValidation } from 'gw-portals-validation-react';
import SpmVehicleModificationForm from 'sg-spm-vehicle-modifications-form';
import { TranslatorContext } from '@jutro/locale';
import { publish } from '@jutro/events';
import ADOBE_TOPICS from 'sg-adobe-analytics/AdobeTopics';
import { SG_AA_PROD_ACTION_LOC } from 'sg-adobe-analytics/AdobeAnalyticsConstants';
import SpmVehicleModificationsTable from './SgVehicleModificationsTable/SpmVehicleModificationsTable';
import messages from './SpmVehicleModifications.messages';
import metadata from './SpmVehicleModifications.metadata.json5';

function SpmVehicleModifications(props) {
    const {
        id,
        modelVM,
        updateData,
        showErrors,
        onValidate: onValidateParent,
        messageParams,
        secondaryLabel
    } = props;
    const {
        isComponentValid,
        onValidate
    } = useValidation(id);

    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const vehicleVM = modelVM.lobData.privateMotor.coverables.spmVehicle;
    const [showModificationForm, setShowModificationForm] = useState(false);
    const [isInitEditRender, setIsInitEditRender] = useState(false);
    const [selectedModIndex, setSelectedModIndex] = useState(null);
    const [displayedModifications, setDisplayedModifications] = useState(
        vehicleVM.vehicleModifications.length ? _.clone(vehicleVM.vehicleModifications.children) : []
    );
    const [modificationInEdit, setModificationInEdit] = useState(null);

    useEffect(() => {
        if (showModificationForm && isComponentValid) {
            onValidateParent(false, id);
        } else {
            onValidateParent(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidateParent, showModificationForm]);

    const addModification = useCallback(() => {
        if (vehicleVM.vehicleModifications.value === undefined) {
            vehicleVM.vehicleModifications = [{}];
        } else {
            vehicleVM.vehicleModifications.pushElement({});
        }
        setSelectedModIndex(_.indexOf(vehicleVM.vehicleModifications.value,
            _.last(vehicleVM.vehicleModifications.value)));
        setShowModificationForm(true);
    }, [vehicleVM]);

    const editModification = useCallback((index) => {
        setModificationInEdit(viewModelService.clone(displayedModifications[index]));
        const newDisplayedModifications = displayedModifications.filter((e) => _.indexOf(displayedModifications, e) !== index);
        setDisplayedModifications(newDisplayedModifications);
        setSelectedModIndex(index);
        setShowModificationForm(true);
        setIsInitEditRender(true);
    }, [displayedModifications, viewModelService]);

    const trackModificationChange = useCallback(() => {
        if (vehicleVM.vehicleModifications.value) {
            const modifications = vehicleVM.vehicleModifications.value;
            publish(ADOBE_TOPICS.MODIFICATION_CHANGE, { model: modifications, productActionLocation: SG_AA_PROD_ACTION_LOC.BEFORE_PREMIUM_PAGE });
        }
    }, [vehicleVM]);

    const refreshDisplayedModifications = useCallback(() => {
        setDisplayedModifications(_.clone(vehicleVM.vehicleModifications.children));
        trackModificationChange();
    }, [trackModificationChange, vehicleVM]);

    const removeModification = useCallback((index) => {
        _.pullAt(vehicleVM.vehicleModifications.value, [index]);
        if (!vehicleVM.vehicleModifications.length) {
            vehicleVM.hasModifications.value = false;
            setDisplayedModifications([]);
        } else {
            refreshDisplayedModifications();
        }
    }, [vehicleVM, refreshDisplayedModifications]);

    const cancelOrRemoveModification = useCallback((index) => {
        if (!_.isNil(modificationInEdit)) {
            vehicleVM.vehicleModifications.value[index] = modificationInEdit.value;
            setModificationInEdit(null);
            refreshDisplayedModifications();
        } else {
            removeModification(index);
        }
    }, [vehicleVM, modificationInEdit, removeModification, refreshDisplayedModifications]);

    const hideModificationForm = useCallback(() => {
        setShowModificationForm(false);
    });

    useEffect(() => {
        if (vehicleVM.hasModifications.value) {
            if (displayedModifications.length === 0) {
                addModification();
            }
        } else {
            vehicleVM.vehicleModifications.value = undefined;
            setShowModificationForm(false);
            setDisplayedModifications([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicleVM.hasModifications.value]);

    const isAddModificationVisible = () => {
        return !_.isNil(vehicleVM.hasModifications.value) && vehicleVM.hasModifications.value && !showModificationForm;
    };

    const overrideProps = {
        hasModifications: {
            label: translator(messages.VehicleModifiedLabel, messageParams)
        },
        modificationsTable: {
            displayedModifications: displayedModifications,
            visible: !_.isEmpty(displayedModifications),
            disableEditButton: showModificationForm,
            editModification: editModification,
            removeModification: removeModification
        },
        modificationForm: {
            visible: showModificationForm,
            modelVM,
            updateData,
            modificationIndex: selectedModIndex,
            hideModificationForm: hideModificationForm,
            cancelOrRemoveModification: cancelOrRemoveModification,
            refreshDisplayedModifications: refreshDisplayedModifications,
            onValidate: onValidate,
            messageParams,
            isInitEditRender,
            setIsInitEditRender
        },
        addModificationButton: {
            visible: isAddModificationVisible()
        }
    };

    if (secondaryLabel) {
        overrideProps.hasModifications.secondaryLabel = secondaryLabel;
    }

    const resolvers = {
        resolveCallbackMap: {
            addModification: addModification
        },
        resolveComponentMap: {
            SpmVehicleModificationForm,
            SpmVehicleModificationsTable
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={modelVM}
            overrideProps={overrideProps}
            onModelChange={updateData}
            showErrors={showErrors}
            onValidationChange={onValidate}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
        />
    );
}

SpmVehicleModifications.propTypes = {
    id: PropTypes.string,
    modelVM: PropTypes.shape({}).isRequired,
    updateData: PropTypes.func.isRequired,
    showErrors: PropTypes.bool
};

SpmVehicleModifications.defaultProps = {
    id: undefined,
    showErrors: undefined
};

export default SpmVehicleModifications;
